/**
 * NAV-DASHBOARD
 * Container for dashboard page
 * Back to list of survey button & header
 */

import { withTranslation, WithTranslation } from "react-i18next"
import NavFocus, { NavFocusAction } from "./nav.focus"
import DashboardHeaderWidget from "@/widgets/dashboard-header.widget"
import { faClipboardList, faCog, faDownload, faThumbtack } from "@fortawesome/free-solid-svg-icons"
import { SurveyState } from "@/redux/survey.types"
import { connect } from "react-redux"
import { useState } from "react"
import Modal from "@/components/modal"
import { BookmarkState } from "@/redux/bookmark.types"
import BookmarksWidget from "@/widgets/bookmarks.widget"
import AddBookmarkModal from "@/modals/add-bookmark.modal"
import ExportDashboardModal from "@/modals/export-dashboard.modal"
import SurveyOptionsHelp from "@/help/survey-options.help"
import ButtonScrolltop from "@/components/button-scrolltop"
import { Session, SessionDashboardTab } from "@/redux/_session.types"
import { TopicState } from "@/redux/topic.types"
import { MessageState } from "@/redux/message.types"
import LoadingModal from "@/modals/loading.modal"
import { store } from "@/index"
import { filterReplaceDashboard } from "@/redux/filter.actions"
import { DashboardFilters } from "@/redux/filter.types"
import { projectActivate, projectFetchOne } from "@/redux/project.actions"
import { toast } from "react-toastify"
import { NavigateFunction, useLocation, useNavigate } from "react-router-dom"
import TasksLinkerWidget from "@/widgets/tasks-linker.widget"
import { ActionPlan, ActionPlanState } from "@/redux/action-plan.types"
import TasksActionsWidget from "@/widgets/tasks-actions.widget"
import { v4 as uuid } from "uuid"
import { actionPlanActivate, actionPlanUpdate } from "@/redux/action-plan.actions"
import { userActivate } from "@/redux/user.actions"
import { User } from "@/redux/user.types"
import HelpGuideProvider from "@/widgets/help-guide-provider.widget"

interface StateProps {
  _session: Session
  actionPlan: ActionPlanState
  bookmark: BookmarkState
  message: MessageState
  survey: SurveyState
  topic: TopicState
}

interface OwnProps {
  status?: string //Display loader
  children: any //Content inside the page
  isFullscreen?: boolean //Display page in fullscreen
  helpKey?: string //Key of the help article of the page (written in Strapi)
  hideFilters?: boolean //Hide filters
  hideOptions?: boolean //Hide options
  showSurveySelector?: boolean //Show dropdown to switch current survey
  title?: string //Title for page (if not survey name)
}

type Props = StateProps & OwnProps & WithTranslation

const ACTION_PLAN: string = "ACTION_PLAN"
const MODAL_BOOKMARK: string = "MODAL_BOOKMARK"
const MODAL_BOOKMARK_UNAVAILABLE: string = "MODAL_BOOKMARK_UNAVAILABLE"
const MODAL_EXPORT: string = "MODAL_EXPORT"
const MODAL_ADMIN_UNAVAILABLE: string = "MODAL_ADMIN_UNAVAILABLE"
const MODAL_LOADING: string = "MODAL_LOADING"
const MODAL_HELP_SETTINGS: string = "MODAL_HELP_SETTINGS"
const MODAL_NOT_ADMIN: string = "MODAL_NOT_ADMIN"

function NavDashboard(props: Props) {
  const { t } = props
  const navigate: NavigateFunction = useNavigate()
  const location = useLocation()

  //Current dialog open
  const [currentModal, setCurrentModal] = useState<string | null>(null)

  //Const
  const hasActionPlan = props.actionPlan.active.SurveyId === props.survey.active.id
  const hasRps = props.topic.list.some((tp) => tp.countRps > 0)
  const hasNps =
    props._session.accountId === "1dbad5e4-93d4-4d85-871a-21dc9a17a0ad" ||
    props.topic.list.some((tp) => tp.countNps > 0)

  const actionPlanTab = window.location.pathname.indexOf("/tasks/edit") > -1

  // Compter les sondages du projet actif
  // const surveysCount = props.survey.list.filter((x) => x.ProjectId === props.survey.active.ProjectId).length

  //Tabs
  //Magic backend ELEPHANT (no access for managers)
  //if not admin no access to comparator
  const [tabs] = useState<SessionDashboardTab[]>(
    props._session.getDashboardTabs(props.survey.active.randomData, props.survey.list.length)
  )

  //All tabs
  const visibleTabs = tabs.filter((tab) => {
    if (tab.id === "/dashboard/rps" && !hasRps) return false
    if (tab.id === "/dashboard/nps" && !hasNps) return false
    if (tab.id === "/dashboard/messages" && props.message.count === 0) return false
    if (tab.id === "/dashboard/history" && props.survey.list.length <= 1) return false
    return true
  })

  //Add tasks tab
  if (props._session.getModuleObserver("task") && hasActionPlan) {
    visibleTabs.push({
      id: "/tasks/edit",
      icon: faClipboardList,
      label: t("module_task_uniq"),
      isLarge: true
    })
  }

  //Go back from dashboard
  function back() {
    //Page opened from a link that asks to come back with its filters (ex : "see details" in the heatmap)
    if (location.state?.backFilters) {
      store.dispatch(filterReplaceDashboard(new DashboardFilters(location.state.backFilters)))
      navigate(-1)
    } else if (isItemSelected(tabs[0].id)) {
      navigate("/home")
    } else {
      navigate(tabs[0].id)
    }
  }

  //Click on nav button to redirect to page
  async function clickAction(actionId) {
    //Tasks
    if (actionId === "tasks") {
      navigate("/tasks/edit")
    }

    //Action plan create
    else if (actionId === ACTION_PLAN) {
      await createActionPlan()
    }

    //If demo survey survey can not be admin
    else if ((actionId === MODAL_EXPORT || actionId === MODAL_BOOKMARK) && props.survey.active.randomData) {
      setCurrentModal(MODAL_ADMIN_UNAVAILABLE)
    }
    //Admin unavaible for observer
    else if (actionId === "/project/edit" && props._session.userRole === "OBSERVER") {
      setCurrentModal(MODAL_NOT_ADMIN)
    }
    //Open modal export
    else if (actionId === MODAL_EXPORT) {
      setCurrentModal(MODAL_EXPORT)
    }
    //If bookmark
    else if (actionId === MODAL_BOOKMARK) {
      if (props._session.supervisorLevel === 0) {
        setCurrentModal(MODAL_BOOKMARK)
      } else {
        setCurrentModal(MODAL_BOOKMARK_UNAVAILABLE)
      }
    }
    //Change page
    else {
      //Search project associated to the survey
      if (actionId === "/project/edit") {
        const project = await getProject()
        if (project) navigate("/project/edit")
        else toast(t("project_deleted"), { type: "error" })
      } else {
        navigate(actionId)
      }
    }
  }

  async function createActionPlan() {
    setCurrentModal(MODAL_LOADING)

    const currentUser = new User({
      id: props._session.userId,
      email: props._session.email,
      firstname: props._session.userFirstname,
      lastname: props._session.userLastname,
      role: props._session.userRole
    })

    const actionPlan: any = await store.dispatch(
      actionPlanUpdate(
        new ActionPlan({
          id: uuid(),
          SurveyId: props.survey.active.id,
          UserId: currentUser.id
        })
      )
    )

    if (actionPlan.error) {
      setCurrentModal(null)
      toast(t(actionPlan.error), { type: "error" })
    } else {
      store.dispatch(
        actionPlanActivate(new ActionPlan({ ...actionPlan, User: currentUser, Survey: props.survey.active }))
      )
      store.dispatch(userActivate(currentUser))
      setCurrentModal(null)
      navigate("/tasks/wizard")
    }
  }

  //Init actions
  //Highlight export for observer
  //Highlight manage participants for admin
  function getActions(): NavFocusAction[] {
    const actions: NavFocusAction[] = [
      { id: MODAL_BOOKMARK, icon: faThumbtack, label: t("utils_pin") },
      {
        id: MODAL_EXPORT,
        icon: faDownload,
        label: t("utils_export"),
        isLarge: true,
        className: ""
      }
    ]

    //If no action plan for the survey
    if (!hasActionPlan && props._session.getModuleObserver("task")) {
      actions.push({
        id: ACTION_PLAN,
        icon: faClipboardList,
        label: t("tasks_add"),
        isLarge: true,
        className: props._session.userRole === "OBSERVER" ? "primary" : ""
      })
    }

    if (props._session.userRole !== "OBSERVER") {
      actions.push({
        id: "/project/edit",
        icon: faCog,
        label: t("survey_link_admin"),
        className: props.survey.active.status === "closed" ? "light" : "primary",
        isLarge: true
      })
    }

    return actions
  }

  //Detect if item is selected
  function isItemSelected(itemId: string) {
    return itemId === window.location.pathname
  }

  //Get project
  async function getProject() {
    if (props.survey.active.ProjectId) {
      setCurrentModal(MODAL_LOADING)
      const project: any = await store.dispatch(projectFetchOne(props.survey.active.ProjectId))
      store.dispatch(projectActivate(project))
      setCurrentModal(null)
      return project
    } else {
      return null
    }
  }

  return (
    <NavFocus
      status={props.status}
      onBack={back}
      onAction={clickAction}
      isWithMarginTop={props.bookmark.list.length > 0}
      isNotScrollable={props.isFullscreen}
      isWithoutPadding={props.isFullscreen}
      isFullWidth={props.isFullscreen}
      tabs={visibleTabs}
      customActions={actionPlanTab ? <TasksActionsWidget /> : undefined}
      actions={actionPlanTab ? undefined : getActions()}>
      {currentModal === MODAL_BOOKMARK_UNAVAILABLE && (
        <Modal isCloseButtonVisible onClose={() => setCurrentModal(null)}>
          <b>{t("dashboard_bookmarks_supervisor")}</b>
        </Modal>
      )}

      {currentModal === MODAL_LOADING && <LoadingModal />}

      {currentModal === MODAL_NOT_ADMIN && (
        <Modal isCloseButtonVisible onClose={() => setCurrentModal(null)}>
          <b>{t("dashboard_is_not_admin")}</b>
        </Modal>
      )}

      {currentModal === MODAL_HELP_SETTINGS && <SurveyOptionsHelp onClose={() => setCurrentModal(null)} />}

      {currentModal === MODAL_BOOKMARK && <AddBookmarkModal onClose={() => setCurrentModal(null)} />}

      {currentModal === MODAL_EXPORT && <ExportDashboardModal onClose={() => setCurrentModal(null)} />}

      <BookmarksWidget />

      <HelpGuideProvider
        helpKey={props.helpKey}
        userRole={props._session.userRole}
        isGollac={props.survey.active.template.options.isGollac}>
      {!props.isFullscreen && (
        <DashboardHeaderWidget
          title={props.title}
          hideFilters={props.hideFilters}
          onOptions={props.hideOptions ? undefined : () => setCurrentModal(MODAL_HELP_SETTINGS)}
          showSurveySelector={props.showSurveySelector}
        />
      )}

      {props.children}
      </HelpGuideProvider>

      {!actionPlanTab && <TasksLinkerWidget surveyId={props.survey.active.id} />}

      <ButtonScrolltop />
    </NavFocus>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  actionPlan: state.actionPlan,
  bookmark: state.bookmark,
  message: state.message,
  survey: state.survey,
  topic: state.topic
})

export default connect(mapStateToProps)(withTranslation()(NavDashboard))
